// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import BoardCreateCollaborators from 'app/common/react/components/BoardCreate/BoardCreateCollaborators';
import CONST from 'app/common/lib/Const';
import i18n from 'app/common/lib/i18n';
import type { AdvertiserType } from 'app/packages/pinterest-flow-types/sterling';
import type { Dispatch } from 'redux';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import { createBoard } from 'app/common/redux/actions/boards';
import { Heading } from 'gestalt';
import BoardForm, { type FieldValues } from 'app/common/react/components/BoardForm/BoardForm';
import type { BoardPrivacy, ProfileBoard } from 'app/packages/pinterest-flow-types/board';
import { inviteEmails, inviteUsers } from 'app/common/redux/actions/board/collab';

const GA_CATEGORY_BOARD_CREATE = 'board_create';

type OwnProps = {|
  categories: Array<{ key: string, name: string }>,
  category?: string,
  collab_board_email?: boolean,
  collaborator_invites_enabled?: boolean,
  description?: string,
  id?: string, // TODO(kathy): resolve flow action creator warnings
  name?: string,
  noHeader?: boolean,
  onBoardCreate: (board: ProfileBoard) => void,
  onClose: () => void,
  privacy: BoardPrivacy,
  suggestedBoardName: string,
|};

type DispatchProps = {
  dispatch: Dispatch<any>,
};

type ReduxProps = {
  advertiserId?: string,
  isAdAccountOwner: boolean,
  username: string,
};

type Props = OwnProps & DispatchProps & ReduxProps;

type UserType = { type: 'user', id: string };
type EmailType = { type: 'email', id: string, address?: string };
export type CollaboratorType = UserType | EmailType;

type State = {
  creating: boolean,
  selectedCollaborators: Array<CollaboratorType>,
};

export class BoardCreate extends Component<Props, State> {
  static defaultProps = {
    privacy: CONST.BoardPrivacy.PUBLIC,
  };

  state = {
    creating: false,
    selectedCollaborators: [],
  };

  handleSubmit = (fieldValues: FieldValues) => {
    const { advertiserId, dispatch, isAdAccountOwner, onBoardCreate } = this.props;
    const {
      name,
      description,
      category,
      privacy,
      collab_board_email,
      collaborator_invites_enabled,
    } = fieldValues;
    const boardData = {
      name,
      description,
      category,
      privacy,
      collab_board_email,
      collaborator_invites_enabled,
      ...(!isAdAccountOwner && advertiserId ? { advertiser_id: advertiserId } : {}),
    };
    this.setState({ creating: true });
    return dispatch(createBoard(boardData))
      .then((newBoard) => {
        this.inviteSelectedColaborators(newBoard.id);
        onBoardCreate(newBoard);
      })
      .finally(() => this.setState({ creating: false }));
  };

  handleSelectedCollaboratorsChange = (selectedCollaborators: Array<CollaboratorType>) => {
    this.setState({ selectedCollaborators });
  };

  inviteSelectedColaborators(boardId: string) {
    const emails: Array<EmailType> = (this.state.selectedCollaborators.filter(
      ({ type }) => type === 'email',
    ): Array<any>);
    if (emails.length >= 1) {
      inviteEmails(boardId, emails.map(({ type }) => type));
    }
    const users: Array<UserType> = (this.state.selectedCollaborators.filter(({ type }) =>
      type.startsWith('user'),
    ): any[]);
    if (users.length >= 1) {
      inviteUsers(boardId, users.map(({ id }) => id));
    }
  }

  emptyBoardData: Object = {
    collab_board_email: true,
    collaborator_invites_enabled: false,
    id: '',
    is_collaborative: false,
    name: this.props.suggestedBoardName || '',
    description: '',
    category: 'other',
    owner: { username: this.props.username },
    pin_count: 0,
  };

  render() {
    const { categories, isAdAccountOwner, noHeader, onClose, privacy } = this.props;
    return (
      <BoardForm
        board={{
          ...this.emptyBoardData,
          ...{ privacy: isAdAccountOwner ? privacy : CONST.BoardPrivacy.PROTECTED },
        }}
        boardCollaboratorsSection={
          <div>
            <BoardCreateCollaborators
              onSelectedCollaboratorsChange={this.handleSelectedCollaboratorsChange}
            />
          </div>
        }
        categories={categories}
        currentUserIsOwner
        gaCategory={GA_CATEGORY_BOARD_CREATE}
        isAdAccountOwner={isAdAccountOwner}
        isDeletable={false}
        isEditable
        isNameAndSecretView
        noHeader={noHeader}
        onClose={onClose}
        onSubmit={this.handleSubmit}
        submitText={i18n._('Create')}
        title={<Heading size="xs">{i18n._('Create board')}</Heading>}
      />
    );
  }
}

const mapStateToProps = (
  state: ReduxState & { advertiser?: AdvertiserType },
): $Exact<ReduxProps> => {
  const { advertiser, viewer } = state;
  const isAdAccountOwner = !advertiser || (viewer.isAuth && advertiser.owner_user_id === viewer.id);
  const advertiser_id = advertiser && advertiser.id;
  return {
    isAdAccountOwner,
    username: viewer.isAuth ? viewer.username : '',
    ...(advertiser_id ? { advertiserId: advertiser_id } : {}),
  };
};

// $FlowFixMe: new errors found from flow 0.96 upgrade
export default connect(mapStateToProps)(BoardCreate);
